import { z } from 'zod';
import { IsoDate, Timestamp, Uuid } from './envelope';

/**
 * The assistant's read models.
 *
 * The shape here is the argument. An answer is not a string: it is a string
 * plus the figures it stood on, each carrying the metric id, the period, the
 * definition version and the route to the screen it came from — so a reader can
 * check any sentence in it against the product rather than believing it.
 *
 * `answer` and `refusalReason` are mutually exclusive and both nullable,
 * because a refusal is a correct outcome rather than an error. "This platform
 * does not record staff attrition" is the right answer to a question about
 * staff attrition, and a contract that could only carry an answer would push
 * the model toward inventing one.
 */

export const AssistantFigureStatus = z.enum([
  'Available',
  'Partial',
  'Stale',
  'Unavailable',
  'Error',
]);
export type AssistantFigureStatus = z.infer<typeof AssistantFigureStatus>;

/**
 * One figure the answer stood on.
 *
 * Built from what the read model actually returned, never from what the model
 * said it saw. A citation the tools did not produce does not appear here — it
 * appears in `unverifiedCitations`, which is the interesting field.
 */
export const AssistantCitation = z.object({
  metricId: z.string().regex(/^MET-\d{3}$/),
  label: z.string(),
  /** Formatted exactly as the screens format it, so the two agree on sight. */
  value: z.string(),
  status: AssistantFigureStatus,
  /** Why the figure is absent, in the reader's language. Null when present. */
  unavailableReason: z.string().nullable(),
  periodStart: IsoDate,
  periodEnd: IsoDate,
  definitionVersion: z.string(),
  source: z.string(),
  /** The view it came from, so a reader can go and look. */
  route: z.string().nullable(),
});
export type AssistantCitation = z.infer<typeof AssistantCitation>;

export const AssistantToolCall = z.object({
  name: z.string(),
  /** The governed view that was read. Named so the audit is legible. */
  view: z.string(),
  succeeded: z.boolean(),
});
export type AssistantToolCall = z.infer<typeof AssistantToolCall>;

export const AssistantAnswer = z.object({
  question: z.string(),
  /** Null on a refusal, and then `refusalReason` says why. */
  answer: z.string().nullable(),
  refused: z.boolean(),
  refusalReason: z.string().nullable(),
  citations: z.array(AssistantCitation),
  /**
   * Metric ids the answer cited that no read model returned.
   *
   * Normally empty. When it is not, the answer quoted something the platform
   * did not hand it, and the reader is told before they act on the sentence
   * around it rather than after.
   */
  unverifiedCitations: z.array(z.string()),
  toolsUsed: z.array(AssistantToolCall),
  /** Whose view of the estate this answer was written against. */
  scopeLabel: z.string(),
  /** Fields withheld from this reader. Their absence in the answer is not a gap. */
  maskedFields: z.array(z.string()),
  model: z.string(),
  promptVersion: z.string(),
  askedAt: Timestamp,
});
export type AssistantAnswer = z.infer<typeof AssistantAnswer>;

/**
 * The written portfolio commentary.
 *
 * `body` is what the model wrote and never changes. `editedBody` is what a
 * person made of it, kept separate so "did a human write this sentence" stays
 * answerable. Whichever is sent, the citations underneath are the same figures.
 *
 * `isReused` is the determinism guarantee made visible: true means this exact
 * document was returned from storage for the same scope, period, prompt version
 * and inputs rather than written again. Two people generating the commentary
 * for the same board pack get the same words, and the screen can say so.
 */
export const PortfolioNarrative = z.object({
  id: Uuid,
  scopeLabel: z.string(),
  periodStart: IsoDate,
  periodEnd: IsoDate,
  body: z.string(),
  editedBody: z.string().nullable(),
  editedBy: z.string().nullable(),
  editedAt: Timestamp.nullable(),
  citations: z.array(AssistantCitation),
  model: z.string(),
  promptVersion: z.string(),
  generatedBy: z.string(),
  generatedAt: Timestamp,
  isReused: z.boolean(),
});
export type PortfolioNarrative = z.infer<typeof PortfolioNarrative>;

/**
 * What the interface needs to decide whether to offer the assistant at all.
 *
 * A question box that appears to think and then fails is worse than one that
 * says the assistant is not configured for this deployment, which is why this
 * is a route rather than an assumption baked into the bundle.
 */
export const AssistantAvailability = z.object({
  configured: z.boolean(),
  /** Why not, in the reader's language. Null when it is available. */
  unavailableReason: z.string().nullable(),
  model: z.string().nullable(),
});
export type AssistantAvailability = z.infer<typeof AssistantAvailability>;
